/**
 * AnonimizacaoWizard — Wizard B5 Anonimização LGPD (Brief 5 B5).
 *
 * 5 passos canônicos (ADR-110):
 *   Step 1: Identificar titular + escopo da entidade
 *   Step 2: Selecionar nível anonimização (partial / full / delete_cascade)
 *   Step 3: Preview cascade chain + revisão — NÃO PULÁVEL (§1.2 item 3)
 *   Step 4: Modal destructive (requireConfirmText) — confirmação irreversível
 *   Step 5: Execução + feedback
 *
 * P15 (Privacidade desde o desenho): Step 3 é nonSkippable — garante que DPO
 *   revisou cascade chain antes de executar. Sem scroll-bypass.
 * P17 (Auditabilidade nativa): audit emitido server-side no execute.
 * Princípio §1.2 item 3: ação irreversível SEMPRE exige confirmação explícita.
 *
 * Capability: lgpd-anonymization-execute (DPO exclusivo).
 * Consome DS v2.2.0 Wizard (nonSkippableSteps) + Modal destructive.
 *
 * Decisão executiva W3.2: Step 3 usa scrollRef + IntersectionObserver para
 * detectar "leu até o fim" antes de desbloquear Próximo — same pattern Brief 5 B5.
 */

import { Modal, Wizard } from '@lume/ui-components';
import { useCallback, useEffect, useRef, useState } from 'react';

import { useCapability } from '../../../hooks/use-capability';
import { useInitiateAnonymization } from '../../../hooks/use-lgpd-anonymization';

import { LgpdCascadePreview } from './LgpdCascadePreview';

import type { AnonymizationLevel, LgpdRequestWireItem } from './types';
import type { WizardStep } from '@lume/ui-components';

interface AnonimizacaoWizardProps {
  request: LgpdRequestWireItem;
  onClose: () => void;
  onSuccess: () => void;
}

const ANONYMIZATION_LEVELS: {
  value: AnonymizationLevel;
  label: string;
  description: string;
  severity: 'medium' | 'high' | 'critical';
}[] = [
  {
    value: 'partial',
    label: 'Parcial',
    description:
      'Pseudonimização dos campos identificadores (nome, CPF, email, telefone). Dados de negócio preservados para análise estatística. Reversível somente pelo DPO via documents-redaction-revert.',
    severity: 'medium',
  },
  {
    value: 'full',
    label: 'Completo',
    description:
      'Substituição de todos os dados pessoais por hashes irreversíveis. Conversas e documentos mantidos para compliance. Sem possibilidade de re-identificação.',
    severity: 'high',
  },
  {
    value: 'delete_cascade',
    label: 'Eliminação em cascata ⚠',
    description:
      'Remoção permanente de todos os dados do titular: identidades, conversas, documentos, bonds, audit (exceto registros de compliance). IRREVERSÍVEL. Exige justificativa legal documentada.',
    severity: 'critical',
  },
];

export function AnonimizacaoWizard({
  request,
  onClose,
  onSuccess,
}: AnonimizacaoWizardProps): JSX.Element | null {
  const canExecute = useCapability('lgpd-anonymization-execute');
  const initiateMutation = useInitiateAnonymization();

  const [selectedLevel, setSelectedLevel] = useState<AnonymizationLevel>('partial');
  const [entityId, setEntityId] = useState<string>(request.requesterPersonId ?? '');
  const [entityType, setEntityType] = useState<string>('person');

  // Step 3: scroll-to-bottom lock (§1.2 item 3 inegociável)
  const [step3Scrolled, setStep3Scrolled] = useState(false);
  const cascadeBottomRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const el = cascadeBottomRef.current;
    if (el === null) return;
    const obs = new IntersectionObserver(
      (entries) => {
        if (entries[0]?.isIntersecting === true) {
          setStep3Scrolled(true);
          obs.disconnect();
        }
      },
      { threshold: 1.0 },
    );
    obs.observe(el);
    return () => {
      obs.disconnect();
    };
  }, []);

  // Step 4: Modal destructive confirmação
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [step5State, setStep5State] = useState<'idle' | 'loading' | 'success' | 'error'>('idle');
  const [step5ErrorMsg, setStep5ErrorMsg] = useState('');

  const handleExecute = useCallback(async () => {
    setStep5State('loading');
    try {
      await initiateMutation.mutateAsync({
        entityType,
        entityId,
        level: selectedLevel,
        lgpdRequestId: request.id,
      });
      setStep5State('success');
      onSuccess();
    } catch (err) {
      setStep5State('error');
      setStep5ErrorMsg(err instanceof Error ? err.message : 'Erro desconhecido na execução.');
    }
  }, [entityId, entityType, initiateMutation, onSuccess, request.id, selectedLevel]);

  // If DPO lost capability mid-session — safety gate
  if (!canExecute) {
    return (
      <div
        style={{
          position: 'fixed',
          inset: 0,
          background: 'rgba(0,0,0,0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 9999,
        }}
      >
        <div
          style={{
            background: '#fff',
            borderRadius: 12,
            padding: 32,
            maxWidth: 400,
            textAlign: 'center',
          }}
        >
          <div style={{ fontSize: 32, marginBottom: 12 }}>🔒</div>
          <h3 style={{ fontSize: 16, fontWeight: 700, marginBottom: 8 }}>
            Sem permissão para anonimizar
          </h3>
          <p style={{ fontSize: 13, color: '#6b7280', marginBottom: 16 }}>
            A execução de anonimizações é exclusiva do DPO do tenant.
          </p>
          <button
            type="button"
            onClick={onClose}
            style={{
              padding: '8px 20px',
              borderRadius: 6,
              background: '#6366f1',
              color: '#fff',
              border: 'none',
              cursor: 'pointer',
            }}
          >
            Fechar
          </button>
        </div>
      </div>
    );
  }

  // ─── Steps ──────────────────────────────────────────────────────────────────

  const step1: WizardStep = {
    id: 'step-1-escopo',
    title: 'Titular e escopo',
    content: (
      <Step1EscopoContent
        request={request}
        entityId={entityId}
        entityType={entityType}
        onEntityIdChange={setEntityId}
        onEntityTypeChange={setEntityType}
      />
    ),
    validate: () => {
      if (entityId.trim() === '') return 'ID da entidade é obrigatório';
      return null;
    },
  };

  const step2: WizardStep = {
    id: 'step-2-nivel',
    title: 'Nível de anonimização',
    content: <Step2NivelContent selectedLevel={selectedLevel} onSelect={setSelectedLevel} />,
    validate: () => null,
  };

  // Step 3: NÃO PULÁVEL — scroll-to-bottom destrava Próximo (§1.2 item 3)
  const step3: WizardStep = {
    id: 'step-3-preview',
    title: 'Revisão e cascade ⬇ rolar até o fim',
    content: (
      <Step3PreviewContent
        entityId={entityId}
        request={request}
        selectedLevel={selectedLevel}
        bottomRef={cascadeBottomRef}
        scrolled={step3Scrolled}
      />
    ),
    validate: () => {
      if (!step3Scrolled) {
        return 'Role até o final da pré-visualização para confirmar a revisão do impacto.';
      }
      return null;
    },
  };

  const step4: WizardStep = {
    id: 'step-4-confirmar',
    title: 'Confirmar ação irreversível',
    content: (
      <Step4ConfirmarContent
        selectedLevel={selectedLevel}
        onConfirm={() => {
          setShowConfirmModal(true);
        }}
        isConfirmed={step5State !== 'idle'}
      />
    ),
    validate: () => {
      if (step5State === 'idle') return 'Confirme a ação no botão acima antes de prosseguir.';
      return null;
    },
  };

  const step5: WizardStep = {
    id: 'step-5-execucao',
    title: 'Execução',
    content: <Step5ExecucaoContent state={step5State} errorMsg={step5ErrorMsg} />,
  };

  return (
    <>
      <div
        style={{
          position: 'fixed',
          inset: 0,
          background: 'rgba(0,0,0,0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 9998,
        }}
        onClick={onClose}
        role="dialog"
        aria-modal="true"
        aria-label="Wizard de anonimização LGPD"
      >
        <div
          style={{
            background: '#fff',
            borderRadius: 12,
            width: '90%',
            maxWidth: 720,
            maxHeight: '90vh',
            overflow: 'hidden',
            display: 'flex',
            flexDirection: 'column',
          }}
          onClick={(e) => {
            e.stopPropagation();
          }}
        >
          {/* Cabeçalho */}
          <div
            style={{
              padding: '16px 20px',
              borderBottom: '1px solid #e5e7eb',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
            }}
          >
            <div>
              <div
                style={{
                  fontSize: 10,
                  fontWeight: 600,
                  color: '#9ca3af',
                  textTransform: 'uppercase',
                  letterSpacing: '0.06em',
                  marginBottom: 2,
                }}
              >
                Anonimização · DPO
              </div>
              <h2 style={{ fontSize: 16, fontWeight: 700, margin: 0, color: '#111827' }}>
                Anonimização de Titular
              </h2>
            </div>
            <button
              type="button"
              onClick={onClose}
              aria-label="Fechar wizard"
              style={{
                fontSize: 20,
                color: '#6b7280',
                background: 'none',
                border: 'none',
                cursor: 'pointer',
                lineHeight: 1,
              }}
            >
              ×
            </button>
          </div>

          {/* Wizard */}
          <div style={{ flex: 1, overflow: 'auto' }}>
            <Wizard
              steps={[step1, step2, step3, step4, step5]}
              nonSkippableSteps={[2]} // Step 3 (index 2) = NÃO PULÁVEL
              onComplete={() => {
                if (step5State === 'idle') {
                  void handleExecute();
                } else {
                  onClose();
                }
              }}
              onCancel={onClose}
              nextLabel="Próximo"
              backLabel="Voltar"
              completeLabel={step5State === 'success' ? 'Fechar' : 'Executar anonimização'}
              cancelLabel="Cancelar"
            />
          </div>
        </div>
      </div>

      {/* Modal destructive step 4 — §1.2 item 3 inegociável */}
      {showConfirmModal && (
        <Modal
          isOpen={showConfirmModal}
          onClose={() => {
            setShowConfirmModal(false);
          }}
          title="Confirmar anonimização irreversível"
          destructive
          requireConfirmText="ANONIMIZAR"
          confirmLabel="Confirmar anonimização"
          cancelLabel="Cancelar"
          onConfirm={() => {
            setShowConfirmModal(false);
            void handleExecute();
          }}
        >
          <p style={{ fontSize: 13, color: '#374151', lineHeight: 1.6 }}>
            Você está prestes a iniciar anonimização{' '}
            <strong>NÍVEL {selectedLevel.toUpperCase()}</strong> para{' '}
            <strong>{request.requesterSelfDeclaredData?.name ?? 'Titular'}</strong>.
          </p>
          <p style={{ fontSize: 12, color: '#ef4444', marginTop: 8 }}>
            Esta ação é <strong>IRREVERSÍVEL</strong> e será registrada em audit. Para continuar,
            digite <code>ANONIMIZAR</code> no campo abaixo.
          </p>
        </Modal>
      )}
    </>
  );
}

// ─── Step sub-components ──────────────────────────────────────────────────────

function Step1EscopoContent({
  request,
  entityId,
  entityType,
  onEntityIdChange,
  onEntityTypeChange,
}: {
  request: LgpdRequestWireItem;
  entityId: string;
  entityType: string;
  onEntityIdChange: (v: string) => void;
  onEntityTypeChange: (v: string) => void;
}): JSX.Element {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16, padding: '8px 0' }}>
      <Banner type="info">
        Identifique o titular e confirme o escopo da entidade a ser anonimizada. Princípio §1.2 item
        2: identidade deve ser validada antes de prosseguir.
      </Banner>
      <div>
        <label style={labelStyle}>Titular (da solicitação)</label>
        <div
          style={{
            fontSize: 13,
            color: '#111827',
            padding: '8px 12px',
            background: '#f9fafb',
            borderRadius: 6,
            border: '1px solid #e5e7eb',
          }}
        >
          {request.requesterSelfDeclaredData?.name ?? 'Não identificado'} ·{' '}
          <span style={{ fontFamily: 'monospace', color: '#6b7280' }}>
            {request.requesterSelfDeclaredData?.cpf
              ? `***.***.***-${request.requesterSelfDeclaredData.cpf.slice(-2)}`
              : '—'}
          </span>
        </div>
      </div>
      <div>
        <label style={labelStyle}>Tipo de entidade</label>
        <select
          value={entityType}
          onChange={(e) => {
            onEntityTypeChange(e.target.value);
          }}
          style={inputStyle}
          aria-label="Tipo de entidade"
        >
          <option value="person">Person (titular LGPD)</option>
          <option value="identity">Identity (canal específico)</option>
          <option value="conversation">Conversation</option>
        </select>
      </div>
      <div>
        <label style={labelStyle}>
          ID da entidade{' '}
          {entityType === 'person' && request.requesterPersonId !== null && (
            <span style={{ fontSize: 10, color: '#6366f1' }}>
              · preenchido automaticamente do Person vinculado
            </span>
          )}
        </label>
        <input
          type="text"
          value={entityId}
          onChange={(e) => {
            onEntityIdChange(e.target.value);
          }}
          placeholder="person_xxxxxx / identity_xxxxxx / conv_xxxxxx"
          style={inputStyle}
          aria-label="ID da entidade"
        />
      </div>
    </div>
  );
}

function Step2NivelContent({
  selectedLevel,
  onSelect,
}: {
  selectedLevel: AnonymizationLevel;
  onSelect: (level: AnonymizationLevel) => void;
}): JSX.Element {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 12, padding: '8px 0' }}>
      <Banner type="warn">
        Escolha o nível de anonimização. Níveis mais restritivos são irreversíveis. Solo: apenas
        nível Parcial disponível.
      </Banner>
      {ANONYMIZATION_LEVELS.map((level) => (
        <button
          key={level.value}
          type="button"
          role="radio"
          aria-checked={selectedLevel === level.value}
          onClick={() => {
            onSelect(level.value);
          }}
          style={{
            display: 'flex',
            gap: 12,
            padding: '12px 16px',
            borderRadius: 8,
            border: '2px solid',
            borderColor: selectedLevel === level.value ? '#6366f1' : '#e5e7eb',
            background: selectedLevel === level.value ? '#ede9fe' : '#fff',
            textAlign: 'left',
            cursor: 'pointer',
            transition: 'border-color 0.1s, background 0.1s',
          }}
        >
          <div
            style={{
              width: 20,
              height: 20,
              borderRadius: '50%',
              border: '2px solid',
              borderColor: selectedLevel === level.value ? '#6366f1' : '#d1d5db',
              background: selectedLevel === level.value ? '#6366f1' : '#fff',
              flexShrink: 0,
              marginTop: 2,
            }}
          />
          <div>
            <div
              style={{
                fontSize: 13,
                fontWeight: 700,
                color:
                  level.severity === 'critical'
                    ? '#ef4444'
                    : level.severity === 'high'
                      ? '#f59e0b'
                      : '#111827',
                marginBottom: 4,
              }}
            >
              {level.label}
            </div>
            <div style={{ fontSize: 12, color: '#6b7280', lineHeight: 1.5 }}>
              {level.description}
            </div>
          </div>
        </button>
      ))}
    </div>
  );
}

function Step3PreviewContent({
  entityId,
  request,
  selectedLevel,
  bottomRef,
  scrolled,
}: {
  entityId: string;
  request: LgpdRequestWireItem;
  selectedLevel: AnonymizationLevel;
  bottomRef: React.RefObject<HTMLDivElement | null>;
  scrolled: boolean;
}): JSX.Element {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 12, padding: '8px 0' }}>
      {/* §1.2 item 3 — role até o fim para desbloquear */}
      <Banner type="warn">
        <strong>Role até o final</strong> para revisar todo o impacto antes de prosseguir. Este
        passo não pode ser pulado.
      </Banner>

      {/* Resumo da ação */}
      <div
        style={{
          padding: '10px 14px',
          background: '#fef2f2',
          border: '1px solid #fca5a5',
          borderRadius: 6,
          fontSize: 12,
        }}
      >
        <strong>Resumo:</strong> Anonimização <strong>{selectedLevel}</strong> para{' '}
        <strong>{request.requesterSelfDeclaredData?.name ?? 'Titular'}</strong> · entity{' '}
        <code style={{ fontFamily: 'monospace' }}>{entityId.slice(0, 20)}</code>.
        {selectedLevel === 'delete_cascade' && (
          <span style={{ color: '#ef4444', fontWeight: 700 }}>
            {' '}
            AÇÃO IRREVERSÍVEL — ELIMINAÇÃO TOTAL.
          </span>
        )}
      </div>

      {/* Cascade chain preview — D-B próprio gate dentro */}
      <div style={{ minHeight: 200 }}>
        <LgpdCascadePreview
          entityId={request.id}
          entityLabel={`Solicitação ${request.id.slice(0, 12)}`}
        />
      </div>

      {/* Âncora IntersectionObserver — detecta scroll até fim */}
      <div ref={bottomRef} style={{ height: 1 }} aria-hidden="true" />

      {scrolled ? (
        <div
          style={{
            padding: '8px 12px',
            background: '#f0fdf4',
            border: '1px solid #86efac',
            borderRadius: 6,
            fontSize: 12,
            color: '#166534',
            fontWeight: 600,
          }}
        >
          ✓ Revisão concluída. Você pode prosseguir.
        </div>
      ) : (
        <div
          style={{
            padding: '8px 12px',
            background: '#fef9c3',
            border: '1px solid #fde68a',
            borderRadius: 6,
            fontSize: 12,
            color: '#92400e',
          }}
        >
          ↓ Role até o final para desbloquear o botão Próximo.
        </div>
      )}
    </div>
  );
}

function Step4ConfirmarContent({
  selectedLevel,
  onConfirm,
  isConfirmed,
}: {
  selectedLevel: AnonymizationLevel;
  onConfirm: () => void;
  isConfirmed: boolean;
}): JSX.Element {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16, padding: '8px 0' }}>
      <Banner type="danger">
        <strong>Ação irreversível.</strong> Após confirmar, o processo de anonimização será iniciado
        imediatamente. Esta ação não pode ser desfeita. Confirme somente se validou a identidade do
        titular e revisou o cascade preview.
      </Banner>

      <div>
        <div style={{ fontSize: 13, color: '#374151', marginBottom: 4 }}>
          Nível selecionado:{' '}
          <strong
            style={{
              color:
                selectedLevel === 'delete_cascade'
                  ? '#ef4444'
                  : selectedLevel === 'full'
                    ? '#f59e0b'
                    : '#111827',
            }}
          >
            {selectedLevel === 'delete_cascade'
              ? 'Eliminação em cascata ⚠'
              : selectedLevel === 'full'
                ? 'Completo'
                : 'Parcial'}
          </strong>
        </div>
        <div style={{ fontSize: 12, color: '#6b7280' }}>
          6 princípios LGPD §1.2 aplicados: minimização · finalidade · transparência ·
          responsabilização · incomunicabilidade ·{' '}
          <strong>reversibilidade (IMPOSSÍVEL após confirmação)</strong>.
        </div>
      </div>

      {!isConfirmed ? (
        <button
          type="button"
          onClick={onConfirm}
          style={{
            padding: '10px 20px',
            borderRadius: 8,
            border: '2px solid #ef4444',
            background: '#fef2f2',
            color: '#b91c1c',
            fontSize: 14,
            fontWeight: 700,
            cursor: 'pointer',
            alignSelf: 'flex-start',
          }}
          data-testid="confirm-irreversible-btn"
        >
          ⚠ Confirmar ação irreversível
        </button>
      ) : (
        <div
          style={{
            padding: '10px 16px',
            background: '#f0fdf4',
            border: '1px solid #86efac',
            borderRadius: 6,
            fontSize: 13,
            color: '#166534',
            fontWeight: 600,
          }}
        >
          ✓ Ação confirmada. Prossiga para execução.
        </div>
      )}
    </div>
  );
}

function Step5ExecucaoContent({
  state,
  errorMsg,
}: {
  state: 'idle' | 'loading' | 'success' | 'error';
  errorMsg: string;
}): JSX.Element {
  if (state === 'loading') {
    return (
      <div style={{ textAlign: 'center', padding: 32 }}>
        <div
          style={{ fontSize: 40, marginBottom: 16, animation: 'pulse 1.5s ease-in-out infinite' }}
        >
          ⏳
        </div>
        <div style={{ fontSize: 14, fontWeight: 600, color: '#111827', marginBottom: 8 }}>
          Executando anonimização...
        </div>
        <div style={{ fontSize: 12, color: '#6b7280' }}>
          Aguarde. O processo pode demorar alguns minutos. Um evento de auditoria será registrado ao
          concluir.
        </div>
      </div>
    );
  }

  if (state === 'success') {
    return (
      <div style={{ textAlign: 'center', padding: 32 }}>
        <div style={{ fontSize: 40, marginBottom: 16 }}>✅</div>
        <div style={{ fontSize: 14, fontWeight: 700, color: '#166534', marginBottom: 8 }}>
          Anonimização concluída
        </div>
        <div style={{ fontSize: 12, color: '#6b7280' }}>
          Processo finalizado com sucesso. Audit event emitido (P17). A solicitação LGPD será
          atualizada automaticamente.
        </div>
      </div>
    );
  }

  if (state === 'error') {
    return (
      <div style={{ textAlign: 'center', padding: 32 }}>
        <div style={{ fontSize: 40, marginBottom: 16 }}>❌</div>
        <div style={{ fontSize: 14, fontWeight: 700, color: '#b91c1c', marginBottom: 8 }}>
          Erro na execução
        </div>
        <div
          style={{
            fontSize: 12,
            color: '#6b7280',
            background: '#fef2f2',
            border: '1px solid #fca5a5',
            borderRadius: 6,
            padding: '8px 12px',
            marginTop: 8,
            fontFamily: 'monospace',
          }}
        >
          {errorMsg}
        </div>
        <div style={{ fontSize: 12, color: '#6b7280', marginTop: 8 }}>
          Os dados NÃO foram alterados nesta falha. Tente novamente.
        </div>
      </div>
    );
  }

  // idle — aguardando exec trigger
  return (
    <div style={{ textAlign: 'center', padding: 32 }}>
      <div style={{ fontSize: 40, marginBottom: 16 }}>⚙️</div>
      <div style={{ fontSize: 14, fontWeight: 600, color: '#111827', marginBottom: 8 }}>
        Pronto para executar
      </div>
      <div style={{ fontSize: 12, color: '#6b7280' }}>
        Clique em "Executar anonimização" para iniciar o processo.
      </div>
    </div>
  );
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

function Banner({
  type,
  children,
}: {
  type: 'info' | 'warn' | 'danger';
  children: React.ReactNode;
}): JSX.Element {
  const styles: Record<string, React.CSSProperties> = {
    info: { background: '#eff6ff', border: '1px solid #bfdbfe', color: '#1e40af' },
    warn: { background: '#fef9c3', border: '1px solid #fde68a', color: '#92400e' },
    danger: { background: '#fef2f2', border: '1px solid #fca5a5', color: '#b91c1c' },
  };
  return (
    <div
      style={{
        ...styles[type],
        borderRadius: 6,
        padding: '8px 12px',
        fontSize: 12,
        lineHeight: 1.6,
      }}
    >
      {children}
    </div>
  );
}

const labelStyle: React.CSSProperties = {
  fontSize: 11,
  fontWeight: 600,
  color: '#374151',
  textTransform: 'uppercase',
  letterSpacing: '0.06em',
  display: 'block',
  marginBottom: 6,
};

const inputStyle: React.CSSProperties = {
  width: '100%',
  padding: '8px 12px',
  borderRadius: 6,
  border: '1px solid #d1d5db',
  fontSize: 13,
  color: '#111827',
  background: '#fff',
};
